import { recreateTrackFromOriginalTranscript } from '../../services/transcriptValidations';
import { AbstractCommand } from '../AbstractCommand';

import type { TranscriptTrack } from '../../models/TranscriptTrack';
import type { VideoEditor } from '../../VideoEditor';

/**
 * Command that restores the transcript by rebuilding the transcript track
 * from the original transcript. Re-creates clip segments for each video
 * element using the original words, then replaces the current track.
 *
 * Undoable – captures the previous transcript track and restores it on undo.
 */
export class RestoreTranscriptFromOriginalCommand extends AbstractCommand {
  private previousTrack: TranscriptTrack | undefined;
  private newTrack: TranscriptTrack | undefined;
  private trackId: string | undefined;

  constructor(editor: VideoEditor) {
    super(editor);
  }

  getName(): string {
    return 'RestoreTranscriptFromOriginalCommand';
  }

  getDescription(): string {
    return 'Restore transcript from original';
  }

  shouldCreateUndoRedoEntry(): boolean {
    return true;
  }

  execute(): boolean {
    const currentTrack = this.editor.getVideoTranscriptTrack();
    if (!currentTrack) {
      console.warn('RestoreTranscriptCommand: no transcript track found');
      return false;
    }

    const originalTranscript = this.editor.getOriginalTranscript();
    if (!originalTranscript) {
      console.warn(
        'RestoreTranscriptCommand: no original transcript available',
      );
      return false;
    }

    this.previousTrack = currentTrack;
    this.trackId = currentTrack.id;

    this.newTrack = recreateTrackFromOriginalTranscript(
      currentTrack,
      originalTranscript,
    );

    this.editor.timeline.replaceTrack(this.trackId!, this.newTrack);
    this.editor.setTranscriptTrack(this.newTrack.id!);

    return true;
  }

  undo(): boolean {
    if (!this.previousTrack || !this.newTrack) {
      return false;
    }

    this.editor.timeline.replaceTrack(this.newTrack.id!, this.previousTrack);
    this.editor.setTranscriptTrack(this.previousTrack.id!);

    return true;
  }
}
